import client from "@/support/Client";
import { InstanceLoader } from "@/support/InstanceLoader";
import { studlyCase } from "@/support/String";
import { AxiosError } from "axios";
import store from "@/store";
import { isNullOrUndefined, isObject } from "util";

export class Model {
  // $name must be exactly the same as the class name.
  protected $name: string = "Model";
  protected $endpoint: string = "";
  protected $primaryKey: string = "id";

  protected $fillable: string[] = [];

  protected $page: number | null = null;
  protected $limit: number | null = null;

  protected $sort: SortOptions = {};

  protected $skip_count: boolean = false;

  public meta?: object = {};

  protected $includes: string[] = [];
  protected filters: object = {};

  // Autocomplete
  protected initialCallSearch: string = '';
  protected initialCallResult: any = null;

  constructor(attributes: object = {}) {
    Object.assign(this, attributes);
  }

  public getFilterLabel(): string {
    if ((this as any)["title"] !== undefined) {
      return (this as any)["title"];
    }

    if ((this as any)["name"] !== undefined) {
      return (this as any)["name"];
    }

    return "UNKNOWN";
  }

  public exists(): boolean {
    return !isNullOrUndefined((this as any)[this.$primaryKey]);
  }

  public first(): Promise<any> {
    return this.limit(1)
      .all()
      .then(
        (models: Model[]) => {
          if (
            models === undefined ||
            !Array.isArray(models) ||
            !(models[0] instanceof Model)
          ) {
            return Promise.reject(models);
          }
          Object.assign(this, models[0]);
          return Promise.resolve(models[0]);
        },
        (error: object) => {
          return Promise.reject(error);
        }
      );
  }

  public all(): Promise<any> {
    const url = this.$endpoint;

    return this.request("get", url);
  }

  // Will cache the result if you use the same model object (new Color() for example)
  public async autocomplete(search?: string) {
    // Always get new result
    if (this.initialCallSearch !== search) {
      return await this.filter({search: search}).all();
    }

    if (! this.initialCallResult) {
      this.initialCallResult = this.filter({search: search}).all();
    }

    if (this.initialCallResult instanceof Promise) {
      return await this.initialCallResult;
    }

    return this.initialCallResult;
  }

  public find(id: any): Promise<any> {
    const url = this.$endpoint + "/" + id;

    return this.request("get", url);
  }

  public create(attributes?: object): Promise<any> {
    const url = this.$endpoint;

    attributes = this.getPayload(attributes);

    return this.request("post", url, attributes);
  }

  public put(attributes?: object): Promise<any> {
    const url = this.$endpoint + "/" + this.resolvePrimaryKey();

    return this.request("put", url, this.getPayload(attributes));
  }

  public update(attributes?: object): Promise<any> {
    const url = `${this.$endpoint}${
      this.resolvePrimaryKey().length ? "/" + this.resolvePrimaryKey() : ""
    }`;
    return this.request("patch", url, this.getPayload(attributes));
  }

  public delete(attributes?: object): Promise<any> {
    const url = this.$endpoint + "/" + this.resolvePrimaryKey();

    return this.request("delete", url, this.getPayload(attributes));
  }

  public deleteMany(ids: string[]) {
    const url = this.$endpoint;

    return this.request("delete", url, { id: ids });
  }

  public getInstanceName() {
    return this.$name;
  }

  public resolvePrimaryKey(): string {
    return (this as any)[this.$primaryKey];
  }

  public getPayload(attributes?: object): object {
    if (attributes === undefined) {
      attributes = { ...{}, ...(this as Object) } as Model;

      const keys = Object.keys(attributes);

      keys.forEach((property, index) => {
        if (!this.isFillable(property)) {
          delete (attributes as any)[property];
        }
      });
    }
    return attributes;
  }

  public request(method: string, url: string, payload: any = {}, version: boolean = true): Promise<any> {
    if (this.$includes.length) {
      payload.with = this.$includes;
    }

    const keys = Object.keys(this.filters);
    if (keys.length) {
      keys.forEach(key => {
        const model: Model = (this.filters as any)[key];
        if (
          isObject(model) &&
          typeof model["resolvePrimaryKey"] === "function"
        ) {
          (this.filters as any)[key] = model.resolvePrimaryKey();
        }
      });

      payload.filters = this.filters;
    }

    if (this.$page !== null) {
      payload.page = this.$page;
    }

    if (this.$limit !== null) {
      payload.limit = this.$limit;
    }

    if (this.$sort.key) {
      payload.sort =
        this.$sort.order === "DESC" ? `!${this.$sort.key}` : this.$sort.key;
    }

    if (this.$skip_count) {
      payload.skip_count = true;
    }

    return client(method, url, payload, version).then(
      (response: object) => {
        return Promise.resolve(this.responseToModel(response));
      },
      (error: AxiosError) => {
        if (error.response !== undefined && error.response.status === 401) {
          store.dispatch("userDeauthenticated");
        }
        return Promise.reject(error);
      }
    );
  }

  public include(includes: string | string[]) {
    if (typeof includes === "string") {
      includes = [includes];
    }

    this.$includes = includes;

    return this;
  }

  public removeFilter(key: string) {
    if ((this.filters as any)[key]) {
      delete (this.filters as any)[key];
    }

    return this;
  }

  public filter(filters: string | object, value?: any) {
    if (typeof filters === "string") {
      const key = filters;
      filters = {};
      (filters as any)[key] = value ? value : null;
    }

    this.filters = { ...this.filters, ...filters };

    return this;
  }

  public page(page: number) {
    this.$page = page;

    return this;
  }

  public limit(limit: number) {
    this.$limit = limit;

    return this;
  }

  public isFillable(key: string): boolean {
    return this.$fillable.includes(key);
  }

  public sort(key: string, order: SortOrder = "ASC") {
    this.$sort = { key, order };

    return this;
  }

  public skipCount() {
    // This is a hack to do simple pagination
    // Can be used for autocompletes to return the count quickly, speeding up response time by 2
    // The count will always be the limit * 2
    this.$skip_count = true;

    return this;
  }

  public clone() {
    return Object.assign(Object.create(Object.getPrototypeOf(this)), this);
  }

  public dataToModel(
    data: any,
    className: string,
    rows: boolean | Object[] = false
  ): any {
    if (rows && Array.isArray(rows)) {
      let meta = {
        current_page: data.current_page || 1,
        first_page: data.from || 1,
        last_page: data.last_page || 1,
        total: data.total || 1
      };

      if (data.totals) {
        meta = { ...meta, ...{ totals: data.totals } };
      }

      rows.forEach((row: any, i: any) => {
        (rows as any)[i] = this.rowToModel(row, className);
        const currentMeta =
          (rows as any)[i].meta !== undefined &&
          typeof (rows as any)[i].meta === "object"
            ? (rows as any)[i].meta
            : {};
        (rows as any)[i].meta = { ...meta, ...currentMeta };
      });

      return rows;
    }

    return this.rowToModel(data, className);
  }

  public responseToModel(response: any): any {
    const paginatedResults =
      response.data.data !== undefined && Array.isArray(response.data.data);
    const data = response.data;
    const rows = paginatedResults ? response.data.data : false;
    const className = this.getInstanceName();
    return this.dataToModel(data, className, rows);
  }

  public rowToModel(
    row: object,
    modelClass: string,
    includes: any = []
  ): Model {
    const attributes = row;

    const model = InstanceLoader.get(modelClass, attributes);

    Object.keys(model).forEach(key => {
      const setterMethod = "set" + studlyCase(key) + "Attribute";

      if (model.__proto__.hasOwnProperty(setterMethod)) {
        model[key] = model[setterMethod](model[key]);
      }
    });

    return model;
  }
}

export interface SortOptions {
  key?: string;
  order?: SortOrder;
}

export type SortOrder = "ASC" | "DESC";
